<script>
import { mapGetters } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      showModal: false,
      conversationId: null,
      selectedAgentId: '',
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      getConversationById: 'getConversationById',
      assignableAgentsUiFlags: 'inboxAssignableAgents/getUIFlags',
    }),
    conversation() {
      if (!this.conversationId) {
        return null;
      }
      const conversation =
        this.getConversationById(this.conversationId) ||
        (this.currentChat.id === this.conversationId
          ? this.currentChat
          : null);
      return conversation || null;
    },
    assignableAgents() {
      const inboxId = this.conversation?.inbox_id;
      if (!inboxId) {
        return [];
      }
      return this.$store.getters['inboxAssignableAgents/getAssignableAgents'](
        inboxId
      );
    },
    canSubmit() {
      return Boolean(this.conversationId) && !this.isSubmitting;
    },
    selectedAgent() {
      const selectedId =
        this.selectedAgentId === '' ? null : Number(this.selectedAgentId);
      if (!selectedId) {
        return null;
      }
      return this.assignableAgents.find(agent => agent.id === selectedId);
    },
  },
  watch: {
    conversation: {
      handler(newConversation) {
        if (!newConversation) {
          return;
        }
        const assigneeId = newConversation.meta?.assignee?.id;
        this.selectedAgentId =
          typeof assigneeId === 'number' ? String(assigneeId) : '';
      },
      immediate: true,
    },
  },
  mounted() {
    emitter.on('open-transfer-modal', this.handleOpenModal);
  },
  beforeUnmount() {
    emitter.off('open-transfer-modal', this.handleOpenModal);
  },
  methods: {
    async handleOpenModal(conversationId) {
      if (!conversationId) {
        return;
      }
      this.conversationId = Number(conversationId);
      const conversation = this.conversation;
      if (!conversation) {
        await this.$store.dispatch('getConversation', this.conversationId);
      }
      this.fetchAssignableAgents();
      this.showModal = true;
    },
    fetchAssignableAgents() {
      const inboxId = this.conversation?.inbox_id;
      if (inboxId) {
        this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
      }
    },
    closeModal() {
      this.showModal = false;
      this.isSubmitting = false;
      this.conversationId = null;
      this.selectedAgentId = '';
    },
    async transferConversation() {
      if (!this.conversationId) {
        return;
      }
      this.isSubmitting = true;
      const agentId =
        this.selectedAgentId === '' ? null : Number(this.selectedAgentId);
      try {
        await this.$store.dispatch('assignAgent', {
          conversationId: this.conversationId,
          agentId,
        });
        useAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL',
            {
              conversationId: this.conversationId,
              agentName:
                this.selectedAgent?.name ||
                this.$t('CONVERSATION.DASHBOARD_ACTIONS.UNASSIGNED_OPTION'),
            }
          )
        );
        this.closeModal();
      } catch (error) {
        useAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.FAILED'
          )
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="showModal" :on-close="closeModal">
    <div class="flex flex-col gap-4">
      <woot-modal-header
        :header-title="$t('CONVERSATION.DASHBOARD_ACTIONS.TRANSFER_CHAT')"
        :header-content="
          $t('CONVERSATION.DASHBOARD_ACTIONS.TRANSFER_CHAT_DESCRIPTION')
        "
      />
      <div v-if="assignableAgentsUiFlags.isFetching" class="text-sm">
        {{ $t('CONVERSATION.DASHBOARD_ACTIONS.LOADING_AGENTS') }}
      </div>
      <div v-else class="flex flex-col gap-2">
        <label class="flex flex-col gap-1 text-sm font-medium">
          {{ $t('CONVERSATION.DASHBOARD_ACTIONS.SELECT_AGENT_LABEL') }}
          <select
            v-model="selectedAgentId"
            class="w-full px-3 py-2 text-sm bg-transparent border rounded-md border-n-weak focus:outline-none"
          >
            <option value="">
              {{ $t('CONVERSATION.DASHBOARD_ACTIONS.UNASSIGNED_OPTION') }}
            </option>
            <option
              v-for="agent in assignableAgents"
              :key="agent.id"
              :value="String(agent.id)"
            >
              {{ agent.name }}
            </option>
          </select>
        </label>
        <p
          v-if="!assignableAgents.length"
          class="text-xs text-n-slate-11 leading-tight"
        >
          {{ $t('CONVERSATION.DASHBOARD_ACTIONS.NO_AGENTS_AVAILABLE') }}
        </p>
      </div>
      <div class="flex justify-end gap-2">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('CONVERSATION.DASHBOARD_ACTIONS.CANCEL')"
          @click="closeModal"
        />
        <NextButton
          type="button"
          :label="$t('CONVERSATION.DASHBOARD_ACTIONS.SUBMIT')"
          :loading="isSubmitting"
          :disabled="assignableAgentsUiFlags.isFetching || !canSubmit"
          @click="transferConversation"
        />
      </div>
    </div>
  </woot-modal>
</template>
